// Purpose: Defines BayHive application layout contracts and legacy embedded-view aliases.
import { bayhiveCompactBreakpoint } from './shared.js';
import { bayhiveWorkflowTokens } from './workflow.js';

export const bayhiveExtensions = {
  bayhive: {
    workflow: bayhiveWorkflowTokens,
    touch: {
      targetSize: 'max(44px, 2.75rem)',
    },
    calendar: {
      appointment: {
        background: 'color-mix(in srgb, {primary.color} {bayhive.calendar.appointment.tint}, {bayhive.calendar.appointment.base})',
        tint: '30%',
        base: '{bayhive.neutral.50}',
        darkBase: '{bayhive.neutral.700}',
        color: '{bayhive.neutral.900}',
        darkBackground: 'color-mix(in srgb, {primary.color} {bayhive.calendar.appointment.tint}, {bayhive.calendar.appointment.darkBase})',
        darkColor: '{slate.100}',
        accentColor: '{primary.color}',
        shadow: '0 2px 4px color-mix(in srgb, {bayhive.neutral.900} 18%, transparent)',
        darkShadow: '0 2px 5px rgb(0 0 0 / 32%)',
      },
    },
    // Structural application spacing. These values do not change Lara component density.
    spacing: {
      xs: '0.25rem',
      sm: '0.5rem',
      md: '0.75rem',
      lg: '1rem',
      xl: '1.25rem',
      xxl: '1.5rem',
      xxxl: '2rem',
      xxxxl: '3rem',
    },
    /*
     * Legacy compatibility aliases: embedded Autoflow SCSS consumes these paths directly.
     * Native BayHive components must use Lara's tokens, props, or local dt values instead.
     * Keep their values stable until the corresponding embedded markup is migrated.
     */
    control: {
      height: '2.375rem',
      font: {
        size: '0.875rem',
      },
      padding: {
        x: '0.625rem',
        y: '0.46875rem',
      },
      choice: {
        size: '1.25rem',
        iconSize: '0.875rem',
      },
      sm: {
        height: '2rem',
        font: {
          size: '0.8125rem',
        },
        padding: {
          x: '0.5rem',
          y: '0.3125rem',
        },
        choice: {
          size: '1.125rem',
          iconSize: '0.625rem',
        },
      },
      lg: {
        height: '2.875rem',
        font: {
          size: '1rem',
        },
        padding: {
          x: '0.75rem',
          y: '0.625rem',
        },
        choice: {
          size: '1.5rem',
          iconSize: '1.125rem',
        },
      },
    },
    typography: {
      body: {
        fontSize: '{typography.font.size}',
        lineHeight: '{typography.line.height}',
        // Preserve existing body.small and body.caption token paths for current consumers.
        smallFontSize: '{typography.font.size}',
        captionFontSize: '{bayhive.typography.caption.font.size}',
      },
      compact: {
        fontSize: '{typography.font.size}',
        lineHeight: '1.35',
      },
      caption: {
        fontSize: '0.75rem',
        lineHeight: '1.25',
      },
      overline: {
        fontSize: '0.75rem',
        fontWeight: '600',
        letterSpacing: '0.04em',
        lineHeight: '1.25',
      },
      monospace: {
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
      },
      heading: {
        fontWeight: '600',
        h1: {
          fontSize: '1.625rem',
          lineHeight: '1.15',
        },
        h2: {
          fontSize: '1.5rem',
          lineHeight: '1.2',
        },
        h3: {
          fontSize: '1.25rem',
          lineHeight: '1.25',
        },
        h4: {
          fontSize: '1rem',
          fontWeight: '600',
          lineHeight: '1.25',
        },
      },
      label: {
        fontWeight: '600',
        lineHeight: '1.25',
      },
    },
    // Stable dimensions used by the application shell and responsive compositions.
    layout: {
      dialog: {
        viewportInset: '2rem',
      },
      cardGrid: {
        gap: '{bayhive.spacing.md}',
      },
      section: {
        gap: '{bayhive.spacing.lg}',
      },
      page: {
        gutter: {
          compact: '{bayhive.spacing.lg}',
          standard: '{bayhive.spacing.xxl}',
          wide: '{bayhive.spacing.xxxl}',
        },
      },
      pageHeader: {
        controlWidth: '12rem',
        shopSelectorMaxWidth: '32rem',
        zIndex: '5',
      },
      settings: {
        sidebarWidth: '17.5rem',
        workspaceMinHeight: 'calc(100dvh - 7rem)',
        contentMinHeight: 'calc(100dvh - 12rem)',
        stateMinHeight: '20rem',
      },
      sidebar: {
        width: '18rem',
        iconWidth: '4.5rem',
      },
      topbar: {
        height: '4.5rem',
        zIndex: '10',
        compactMinWidth: '60rem',
        startWidth: '16.5rem',
        compactStartWidth: '13.25rem',
        searchWidth: '12rem',
        searchMinWidth: '7.5rem',
        compactSearchWidth: '8.75rem',
        iconFirstSearchWidth: '10rem',
        scopeMinWidth: '15.5rem',
        compactScopeWidth: '15.5rem',
        iconControlWidth: '4rem',
        scopeOptionsHeight: '18rem',
      },
      breakpoint: {
        mobile: '1180px',
        full: '1320px',
        compact: bayhiveCompactBreakpoint,
      },
      command: {
        width: '56rem',
        contentMinHeight: '30rem',
        viewportInset: '{bayhive.spacing.lg}',
        viewportVerticalReserve: '12rem',
        resultsMinHeight: '12rem',
        resultsMaxHeight: '22rem',
        optionIconWidth: '1.5rem',
        rowMinHeight: '3.25rem',
      },
      organization: {
        groupsTableMinWidth: '48rem',
        assignmentsScrollHeight: '18rem',
        assignmentsTableMinWidth: '20rem',
        editorWidth: '46rem',
        searchWidth: '24rem',
        popoverMaxWidth: '30rem',
      },
      serviceGuide: {
        searchMaxWidth: '28rem',
        stickyOffset: '3.75rem',
        shopsPopoverMaxHeight: '34rem',
      },
      inventory: {
        tableMinWidth: '60rem',
        partColumnWidth: '24rem',
        vendorColumnWidth: '15rem',
        quantityColumnWidth: '8rem',
        priceColumnWidth: '9rem',
        actionsColumnWidth: '4rem',
      },
      unauthorized: {
        cardMaxWidth: '40rem',
        actionMinHeight: '2.5rem',
      },
      impersonation: {
        height: '3.125rem',
        zIndex: '6000',
      },
    },
    skeleton: {
      controlHeight: '2.75rem',
      cardBorderRadius: '{content.border.radius}',
      headingWidth: '22rem',
      headingHeight: '2rem',
      descriptionWidth: '34rem',
      textHeight: '1rem',
      actionWidth: '7rem',
      wideActionWidth: '8.5rem',
      summaryLabelHeight: '0.875rem',
      summaryValueHeight: '2rem',
      toolbarWidth: '20rem',
      primaryColumnMinWidth: '8rem',
      columnMinWidth: '6rem',
    },
  },
};

export default bayhiveExtensions;
